import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../utils/api";
import { GET_FEATURE_FLAGS } from "../api/urls";

export const PREDEFINED_FLAGS = [
  "chat_jess",
  "chat_client",
  "contacts_add_new",
  "contacts_list",
  "task_board",
  "channel_session",
  "business",
  "waha_connection",
  "whatsapp_connection",
  "connectors",
  "connections",
  "tacits",
  "credits",
  "subscription",
  "team_management",
  "workflows",
  "google_auth",
] as const;

export type FlagKey = (typeof PREDEFINED_FLAGS)[number];

type FlagsMap = Record<FlagKey, boolean>;

interface FeatureFlagsContextValue {
  flags: FlagsMap;
  isLoading: boolean;
  hasFlag: (key: FlagKey) => boolean;
}

const defaultFlags: FlagsMap = PREDEFINED_FLAGS.reduce(
  (acc, key) => ({ ...acc, [key]: false }),
  {} as FlagsMap,
);

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: defaultFlags,
  isLoading: true,
  hasFlag: () => false,
});

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [flags, setFlags] = useState<FlagsMap>(defaultFlags);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = React.useRef(false);
  // Track the raw authUser string so we re-fetch when the user logs in
  // (the effect below updates this on every navigation)
  const [authToken, setAuthToken] = useState<string | null>(() =>
    localStorage.getItem("authUser"),
  );
  const location = useLocation();

  // Re-check localStorage on every route change so we pick up a token
  // that was stored after the initial render (e.g. right after login).
  useEffect(() => {
    const current = localStorage.getItem("authUser");
    setAuthToken((prev) => (prev !== current ? current : prev));
  }, [location.pathname]);

  const fetchFlags = React.useCallback(
    (showLoading: boolean) => {
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      if (showLoading) setIsLoading(true);
      api
        .get<Record<string, boolean>>(GET_FEATURE_FLAGS)
        .then((r) => {
          const data = r.data ?? {};
          const merged: FlagsMap = { ...defaultFlags };
          for (const key of PREDEFINED_FLAGS) {
            if (typeof data[key] === "boolean") {
              merged[key] = data[key];
            }
          }
          setFlags(merged);
          hasFetchedRef.current = true;
        })
        .catch(() => {
          // fail closed — manter tudo false se a API falhar
          if (!hasFetchedRef.current) setFlags({ ...defaultFlags });
        })
        .finally(() => {
          if (showLoading) setIsLoading(false);
        });
    },
    [authToken],
  );

  // Initial load (with loading state) when auth token changes
  useEffect(() => {
    hasFetchedRef.current = false;
    fetchFlags(true);
  }, [authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Silent background re-fetch on route changes (no loading state)
  // Debounced to avoid multiple parallel requests on rapid navigation
  useEffect(() => {
    if (!hasFetchedRef.current) return;
    const timer = setTimeout(() => fetchFlags(false), 300);
    return () => clearTimeout(timer);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasFlag = (key: FlagKey): boolean => flags[key] ?? false;

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading, hasFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
